import axios from 'axios';
import { stringifyUrl } from 'query-string';
import React, { FC } from 'react';
import { useAsync } from 'react-use';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import MembershipContainer from '@ui/atoms/MembershipContainer';
import Skeleton from '@ui/atoms/Skeleton';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import RoundedCard from '@ui/molecules/RoundedCard';

import BookingUnavailable from '@ui/organisms/BookingUnavailable/BookingUnavailable.component';
import PageContainer from '@ui/organisms/PageContainer';

import { getServiceDisplayName } from '@helpers/Appointments';

import { interpolate } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { getStateName } from '@utils/intake/stateRequirements';

import { getAuthToken } from '@providers/AuthProvider';

import { cogsworthUrlQuery } from '@components/NewAppointmentBooking/NewAppointmentBooking.component';

import { IBookInPersonAppointmentsContent } from './BookInPersonAppointment.page';

interface IBookInPersonAppointmentsProps {
	pageContent: IBookInPersonAppointmentsContent;
	loadingData: boolean;
	member?: {
		id: string;
		name: string;
		email: string;
		stateOfResidence: string;
	};
}

const BOOKING_ERROR = 'BOOKING_ERROR';

export const SkeletonContainer = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	padding: 2.4rem;
	gap: 2.4rem;
	max-width: 60rem;
`;

const fetchBookingUrl = async (authorizationToken: string) => {
	try {
		const response = await axios.get<{ bookingUrl: string }>(
			stringifyUrl({
				url: `${ApiEndpoints.cogsworth}/get-booking-url/gaht`,
				query: {
					'location-type': 'irl',
				},
			}),
			{
				headers: {
					authorizationToken,
				},
				responseType: 'json',
			}
		);
		return response.data.bookingUrl;
	} catch (e) {
		console.error('Error fetching booking url: ', e);
		return BOOKING_ERROR;
	}
};

const BookInPersonAppointment: FC<IBookInPersonAppointmentsProps> = ({
	pageContent,
	loadingData,
	member,
}) => {
	const { bookingErrorHtml, iframeLoadingDisclaimer, pageHeader, pageInfo, supportDisclaimer } =
		pageContent;

	const { value: bookingUrl, loading: bookingUrlLoading } = useAsync(async () => {
		const token = await getAuthToken();
		return await fetchBookingUrl(token);
	}, []);

	const loading = bookingUrlLoading || loadingData;

	if (loading || !bookingUrl) {
		return (
			<PageContainer>
				<SkeletonContainer>
					<Skeleton fullWidth height="lg" />
					<Skeleton fullWidth repeated={3} height="xxl" gap="md" structure="column" />
				</SkeletonContainer>
			</PageContainer>
		);
	}

	const cogsworthDivStyle = {
		margin: '0.8rem 0',
		borderRadius: ' 5px',
		background: '#fff',
		// TODO simplify to just 780px after upvio migration is finished
		height: bookingUrl.includes('upvio.com') ? '600px' : '780px',
	};

	return (
		<PageContainer>
			<MembershipContainer>
				<FlexColumn gap="md">
					<HeadingText>{pageHeader}</HeadingText>
					<BasicText>{pageInfo}</BasicText>
					<BookingUnavailable>
						{bookingUrl === BOOKING_ERROR ? (
							<RoundedCard bgColor="cardRed">
								<WYSIWYGContent
									dangerouslySetInnerHTML={{
										__html: interpolate(bookingErrorHtml, {
											state: getStateName(member?.stateOfResidence),
											service: getServiceDisplayName(''),
										}),
									}}
								/>
							</RoundedCard>
						) : (
							<>
								<iframe
									style={cogsworthDivStyle}
									title="cogsworthUrlIFrame"
									src={stringifyUrl({
										url: bookingUrl,
										query: cogsworthUrlQuery(
											{ id: member!.id, name: member!.name, email: member!.email },
											'gaht-irl'
										),
									})}
								/>
								<BasicText variant="fineprint">{iframeLoadingDisclaimer}</BasicText>
								<WYSIWYGContent dangerouslySetInnerHTML={{ __html: supportDisclaimer }} />
							</>
						)}
					</BookingUnavailable>
				</FlexColumn>
			</MembershipContainer>
		</PageContainer>
	);
};

export default BookInPersonAppointment;
